import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";
import { DMS_PATHS } from "lib/constants/dms-path-constants";
import { DMSServices } from "lib/services/document-services";
import useDocLinks from "./useDocLinks";

interface UseRoomsState {
  rooms: RoomDetails[];
  error?: string;
  loading?: boolean;
  getRooms: (
    clinicId: string,
    onSuccess?: (rooms: RoomDetails[]) => void
  ) => void;
  addRoom: (
    clinicId: string,
    room: RoomDetails,
    onSuccess?: (rooms: RoomDetails) => void
  ) => void;
  updateRoom: (
    roomId: string,
    room: RoomDetails,
    onSuccess?: (rooms: RoomDetails) => void
  ) => void;
  deleteRoom: (roomId: string, onSuccess?: () => void) => Promise<void>;

  getRoomImages: (
    clinicId: string,
    roomId: string,
    onSuccess?: (images: File[]) => void
  ) => Promise<File[]>;

  deleteRoomImages: (
    clinicId: string,
    roomId: string,
    onSuccess?: (images: string[]) => void
  ) => Promise<string[]>;

  updateRoomImages: (
    clinicId: string,
    roomId: string,
    images: File[],
    onSuccess?: (images: string[]) => void
  ) => Promise<string[]>;
}

const useRooms = (): UseRoomsState => {
  const {
    createDocLink,
    deleteDocLink,
    error: docLinksError,
    isLoading: docLinksLoading,
    getDocLink,
  } = useDocLinks();
  const [rooms, setRooms] = useState<RoomDetails[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [imagesLoading, setImagesLoading] = useState<boolean>(false);
  const [imagesError, setImagesError] = useState<string | null>(null);

  const { getData: getRoomsRequest, loading: getRoomsRequestLoading } =
    useApi<RoomDetails[]>();

  const {
    postData: addRoomRequest,
    loading: addRoomRequestLoading,
    putData: updateRoomRequest,
  } = useApi<RoomDetails>();

  const { deleteData: deleteRoomRequest, loading: deleteRoomRequestLoading } =
    useApi<void>();

  const onFailure = (errorMessage: string) => {
    setError(errorMessage);
  };

  const getRooms = async (
    clinicId: string,
    onSuccess?: (rooms: RoomDetails[]) => void
  ): Promise<void> => {
    setError(null);
    await getRoomsRequest(
      `${API_ROUTES.ROOMS.getRooms.replace("{{CLINIC_ID}}", clinicId)}`,
      (value) => {
        setRooms(value);
        onSuccess?.(value);
      },
      onFailure
    );
  };

  const addRoom = async (
    clinicId: string,
    room: RoomDetails,
    onSuccess?: (rooms: RoomDetails) => void
  ): Promise<void> => {
    setError(null);
    const newRoom: RoomDetails = {
      ...room,
      clinicId: clinicId,
    };

    await addRoomRequest(
      API_ROUTES.ROOMS.addRoom.replace("{{CLINIC_ID}}", clinicId),
      newRoom,
      onSuccess,
      onFailure
    );
  };

  const updateRoom = async (
    roomId: string,
    room: RoomDetails,
    onSuccess?: (rooms: RoomDetails) => void
  ): Promise<void> => {
    setError(null);
    const updatedRoom: RoomDetails = {
      ...room,
      id: roomId,
    };

    await updateRoomRequest(
      API_ROUTES.ROOMS.updateRoom.replace("{{ROOM_ID}}", roomId),
      updatedRoom,
      onSuccess,
      onFailure
    );
  };

  const deleteRoom = async (
    roomId: string,
    onSuccess: () => void
  ): Promise<void> => {
    setError(null);
    await deleteRoomRequest(
      API_ROUTES.ROOMS.deleteRoom.replace("{{ROOM_ID}}", roomId),
      {},
      onSuccess,
      onFailure
    );
  };

  const getRoomImages = async (
    clinicId: string,
    roomId: string,
    onSuccess?: (images: File[]) => void
  ): Promise<File[]> => {
    setError(null);
    setImagesLoading(true);
    setImagesError(null);
    const path =
      DMS_PATHS.IMAGES.ROOM_IMAGES.replace("{{CLINIC_ID}}", clinicId).replace(
        "{{ROOM_ID}}",
        roomId
      ) + "%";

    // Query docLink to get the profile pic URL
    const docLinkDetails: Partial<DocLinkDetails> = {
      parentId: roomId,
      path: path,
    };

    return getDocLink(
      docLinkDetails.parentId,
      docLinkDetails.path,
      (response) => {
        Promise.all(
          response.map(
            async (doc) =>
              await DMSServices.getDocumentFromUrl(doc.url, doc.title)
          )
        ).then((images) => onSuccess?.(images));
      }
    )
      .then(async (response) => {
        const images = await Promise.all(
          response.map(
            async (doc) =>
              await DMSServices.getDocumentFromUrl(doc.url, doc.title)
          )
        );
        return Promise.resolve(images);
      })
      .catch((error) => {
        console.error("Error fetching room images: ", error);
        setError(error.message);
        return Promise.reject(error);
      })
      .finally(() => {
        setImagesLoading(false);
        setImagesError(null);
      });
  };

  const deleteRoomImages = async (
    clinicId: string,
    roomId: string,
    onSuccess?: (images: string[]) => void
  ): Promise<string[]> => {
    setError(null);
    const path =
      DMS_PATHS.IMAGES.ROOM_IMAGES.replace("{{CLINIC_ID}}", clinicId).replace(
        "{{ROOM_ID}}",
        roomId
      ) + "%";

    const response = await deleteDocLink(roomId, path);

    // If response is successful, call onSuccess with an empty array
    if (response?.length > -1) {
      const docUrls = response.map((doc) => doc.url);
      onSuccess?.(docUrls);
      return Promise.resolve(docUrls);
    } else {
      const errorMessage = "Failed to delete room images";
      setError(errorMessage);
      return Promise.reject(errorMessage);
    }
  };

  const updateRoomImages = async (
    clinicId: string,
    roomId: string,
    images: File[],
    onSuccess?: (images: string[]) => void
  ): Promise<string[]> => {
    try {
      setError(null);
      const path = DMS_PATHS.IMAGES.ROOM_IMAGES.replace(
        "{{CLINIC_ID}}",
        clinicId
      ).replace("{{ROOM_ID}}", roomId);

      // Delete existing images before uploading new ones
      try {
        await deleteRoomImages(clinicId, roomId, (deletedImages) => {
          console.log("Deleted images: ", deletedImages);
        });
      } catch (error) {
        // Ignore error if deletion fails, continue with upload
      }

      const response = await Promise.all(
        images.map(async (image) => {
          const docLinkDetails: DocLinkDetails = {
            parentId: roomId,
            parentType: "room",
            path: `${path}/${image.name}`,
            title: image.name,
            url: "", // <-- This will be updated after upload
          };

          // Compress file
          const compressedFile = await DMSServices.compressImage(image);

          // Upload file to DMS
          return await DMSServices.uploadDocument(
            compressedFile,
            docLinkDetails.parentId,
            docLinkDetails.parentType,
            docLinkDetails.path,
            "",
            (response) => {
              // Create DocLink
              docLinkDetails.url = response.url;
              createDocLink(docLinkDetails);
            }
          );
        })
      );

      const imageUrls: string[] = (response ?? []).map((res) => res.url);
      onSuccess?.(imageUrls);

      Promise.resolve(imageUrls);
    } catch (error) {
      console.error("Error updating room images: ", error);
      setError(error.message);
      return Promise.reject(error.message);
    }
  };

  return {
    loading:
      getRoomsRequestLoading ||
      addRoomRequestLoading ||
      deleteRoomRequestLoading ||
      docLinksLoading ||
      imagesLoading,
    error: error || docLinksError || imagesError,
    rooms,
    getRooms,
    addRoom,
    updateRoom,
    deleteRoom,
    getRoomImages,
    deleteRoomImages,
    updateRoomImages,
  };
};

export default useRooms;
